import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';
import { verifySmfProximity } from '../services/geoService';

const TERMINALS = ['Terminal A', 'Terminal B'];
const CHECKPOINTS = ['Standard', 'TSA PreCheck', 'CLEAR'];

export default function Report() {
  const { submitReport, user, airlines, addNotification } = useApp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    airline: '',
    terminal: '',
    checkpoint: 'Standard',
    waitTime: 15,
    tip: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.airline || !formData.terminal) return;

    setLoading(true);
    try {
      const { verified, distance } = await verifySmfProximity(10);
      
      if (!verified) {
        addNotification(`Integrity Check Failed: You are ${distance.toFixed(1)} miles from SMF. You must be within 10 miles to report.`, 'error');
        setLoading(false);
        return;
      }

      await submitReport(formData);
      addNotification('Report verified and published!', 'success');
      navigate('/');
    } catch (err) {
      addNotification(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="p-4 pb-24 md:pb-8 max-w-lg mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Report Wait Time</h2>
        <div className="flex items-center space-x-2 bg-blue-500/10 border border-blue-500/20 p-3 rounded-xl mb-4">
          <SafeIcon icon={FiIcons.FiMapPin} className="text-blue-400 shrink-0" />
          <p className="text-[11px] text-blue-200">
            <strong>Integrity Check Active:</strong> We verify your distance from 6900 Airport Blvd to ensure accurate crowdsourced data.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">Airline</label>
            <select className="w-full bg-slate-850 border border-slate-700 rounded-lg p-3 text-white appearance-none focus:ring-2 focus:ring-amber-500 outline-none" value={formData.airline} onChange={e => setFormData({ ...formData, airline: e.target.value })} required>
              <option value="" disabled>Select Airline</option>
              {airlines.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">Terminal</label>
            <select className="w-full bg-slate-850 border border-slate-700 rounded-lg p-3 text-white appearance-none focus:ring-2 focus:ring-amber-500 outline-none" value={formData.terminal} onChange={e => setFormData({ ...formData, terminal: e.target.value })} required>
              <option value="" disabled>Select Terminal</option>
              {TERMINALS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-300">Checkpoint Type</label>
          <div className="grid grid-cols-3 gap-2">
            {CHECKPOINTS.map(cp => (
              <button key={cp} type="button" onClick={() => setFormData({ ...formData, checkpoint: cp })} className={`py-2 px-1 text-xs font-medium rounded-lg border transition-all ${formData.checkpoint === cp ? 'bg-amber-500/10 border-amber-500 text-amber-400' : 'bg-slate-850 border-slate-700 text-slate-400 hover:border-slate-500'}`}>
                {cp}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4 bg-slate-850 p-4 rounded-xl border border-slate-800">
          <div className="flex justify-between items-center">
            <label className="text-sm font-medium text-slate-300">Estimated Wait</label>
            <span className="text-2xl font-mono font-bold text-amber-400">{formData.waitTime} <span className="text-sm text-slate-400">min</span></span>
          </div>
          <input type="range" min="0" max="90" step="5" value={formData.waitTime} onChange={e => setFormData({ ...formData, waitTime: parseInt(e.target.value) })} className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-amber-500" />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-300">Pro Tip (Optional)</label>
          <textarea maxLength={140} rows={2} placeholder="e.g. Left lane is moving much faster right now." className="w-full bg-slate-850 border border-slate-700 rounded-lg p-3 text-white focus:ring-2 focus:ring-amber-500 outline-none resize-none" value={formData.tip} onChange={e => setFormData({ ...formData, tip: e.target.value })} />
        </div>

        <button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold py-4 rounded-xl shadow-lg transition-all flex items-center justify-center space-x-2">
          {loading ? <SafeIcon icon={FiIcons.FiLoader} className="animate-spin" /> : <SafeIcon icon={FiIcons.FiShield} />}
          <span>Verify & Submit {user.isLoggedIn ? '(+10 XP)' : ''}</span>
        </button>
      </form>
    </motion.div>
  );
}